export const DragItemTypes = {
  QUERY: 'query',
  COLLECTION: 'collection',
  FOLDER: 'folder',
  EDITOR_TAB: 'editor_tab',
  DATASOURCE: 'datasource',
  DATASOURCE_FOLDER: 'datasource_folder',
} as const;

export type DragItemType = typeof DragItemTypes[keyof typeof DragItemTypes];

// Shared flag: true when tabs are being reordered (horizontal drag).
// TabDragLayer checks this to suppress the floating label during reorder.
// ChromeTabs sets/clears this when reorder mode starts/ends.
export const tabReorderActiveRef = { current: false }

export interface QueryDragItem {
  type: typeof DragItemTypes.QUERY;
  id: string;
  name: string;
  collectionId: string;
  folderId?: string;
  orderIndex?: number;
  /** IDs of all selected queries when dragging multiple */
  selectedIds?: string[];
  /** Count of items being dragged (for multi-select) */
  count?: number;
}

export interface CollectionDragItem {
  type: typeof DragItemTypes.COLLECTION;
  id: string;
  name: string;
  description?: string;
}

export interface FolderDragItem {
  type: typeof DragItemTypes.FOLDER;
  id: string;
  name: string;
  description?: string;
  collectionId: string;
  parentFolderId?: string;
}

export interface EditorTabDragItem {
  type: typeof DragItemTypes.EDITOR_TAB;
  id: string;
  label: string;
  content: string;
  isDirty?: boolean;
  savedQueryId?: string;
  savedQueryCollectionId?: string;
  savedQueryName?: string;
  datasourceId?: string;
  databaseName?: string;
  schemaName?: string;
}

// Datasource drag item for connections page
export interface DatasourceDragItem {
  type: typeof DragItemTypes.DATASOURCE;
  id: string;
  name: string;
  folderId?: string | null;
  order: number;
  databaseType?: string;
}

// Datasource folder drag item for connections page
export interface DatasourceFolderDragItem {
  type: typeof DragItemTypes.DATASOURCE_FOLDER;
  id: string;
  name: string;
  parentFolderId?: string | null;
  orderIndex: number;
  datasourceCount?: number;
}

export type DragItem = QueryDragItem | CollectionDragItem | FolderDragItem | EditorTabDragItem | DatasourceDragItem | DatasourceFolderDragItem;

export interface DropTarget {
  type: 'collection' | 'folder' | 'datasource_folder' | 'datasource_root';
  id: string;
  collectionId?: string;
  acceptedTypes: DragItemType[];
  name?: string;
}

// Drop result interface for providing feedback
export interface DropResult {
  success: boolean;
  message?: string;
  targetType: 'collection' | 'folder' | 'datasource_folder' | 'datasource_root';
  targetId: string;
  targetName?: string;
}

